import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState } from "@/components/ui-kit";
import {
  Plus, Send, CheckCircle2, XCircle, MessageSquare, Calendar,
  Trash2, Pencil, Eye, Users2, ChevronRight,
} from "lucide-react";

const PURPOSES = [
  { v: "new_customer", l: "New Customer", color: "purple" },
  { v: "follow_up", l: "Follow-up", color: "blue" },
  { v: "payment_collection", l: "Payment Collection", color: "amber" },
  { v: "order_taking", l: "Order Taking", color: "green" },
  { v: "product_detailing", l: "Product Detailing", color: "blue" },
  { v: "sampling", l: "Sampling", color: "purple" },
  { v: "complaint", l: "Complaint", color: "red" },
  { v: "market_survey", l: "Market Survey", color: "slate" },
  { v: "stock_check", l: "Stock Check", color: "slate" },
  { v: "other", l: "Other", color: "slate" },
];
const PURPOSE_MAP = Object.fromEntries(PURPOSES.map((p) => [p.v, p]));

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const STATUS_COLOR = {
  draft: "slate", pending_approval: "amber", approved: "green",
  rejected: "red", changes_requested: "purple", completed: "blue",
};

function thisWeekSunday() {
  const d = new Date();
  const offset = (d.getDay()) % 7; // 0 = Sun
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
}

export default function WeeklyPlans() {
  const { user } = useAuth();
  const has = (p) => user?.permissions?.includes(p);
  const isSupervisor = has("plans.view_all");

  const [tab, setTab] = useState(isSupervisor ? "team" : "mine");
  const [plans, setPlans] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planDetail, setPlanDetail] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    const params = {};
    if (tab === "team") params.status = statusFilter || undefined;
    else params.status = statusFilter || undefined;
    const [p, d, ph] = await Promise.all([
      api.get("/weekly-plans", { params }),
      api.get("/doctors"),
      api.get("/pharmacies"),
    ]);
    setPlans(p.data);
    setDoctors(d.data);
    setPharmacies(ph.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, statusFilter]);

  const openDetail = async (plan) => {
    const { data } = await api.get(`/weekly-plans/${plan.id}`);
    setPlanDetail(data);
    setSelectedPlan(plan);
  };

  const openBuilder = (plan) => {
    setEditingPlan(plan);
    setBuilderOpen(true);
  };

  const remove = async (plan) => {
    if (!window.confirm(`Delete plan for week of ${plan.week_start}?`)) return;
    try {
      await api.delete(`/weekly-plans/${plan.id}`);
      await load();
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  return (
    <div data-testid="plans-page">
      <PageHeader
        title="Weekly Visit Plans"
        subtitle="Plan, submit for approval, then execute with GPS-tracked check-ins"
        actions={
          has("plans.create") && (
            <Btn onClick={() => openBuilder(null)} data-testid="plan-new-btn">
              <Plus className="w-4 h-4" /> New Weekly Plan
            </Btn>
          )
        }
      />

      <Card className="p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {isSupervisor && (
            <>
              <button
                onClick={() => setTab("team")}
                className={`px-3 py-1.5 rounded ${tab === "team" ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800"}`}
                data-testid="tab-team"
              >
                <Users2 className="w-4 h-4 inline-block me-1" /> Team Plans
              </button>
              <button
                onClick={() => setTab("mine")}
                className={`px-3 py-1.5 rounded ${tab === "mine" ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800"}`}
              >
                <Calendar className="w-4 h-4 inline-block me-1" /> My Plans
              </button>
            </>
          )}
          <div className="ms-auto flex items-center gap-2">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="!py-1">
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="changes_requested">Changes Requested</option>
            </Select>
          </div>
        </div>
      </Card>

      {plans.length === 0 ? (
        <EmptyState message={isSupervisor && tab === "team" ? "No team plans yet." : "No plans yet. Click 'New Weekly Plan' to create one."} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map((p) => {
            const stats = p.stats || {};
            const pct = stats.completion_pct || 0;
            return (
              <Card key={p.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`plan-card-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Week of</div>
                    <div className="font-bold text-lg">{p.week_start}</div>
                    {(isSupervisor || tab === "team") && (
                      <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">👤 {p.rep_name}</div>
                    )}
                  </div>
                  <Badge color={STATUS_COLOR[p.status]}>{p.status.replace("_", " ")}</Badge>
                </div>
                <div className="space-y-1.5 text-sm mb-3">
                  <div className="flex justify-between"><span className="text-slate-500">Targets</span><span className="font-semibold">{stats.total_targets || p.total_targets || 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Completed</span><span className="font-semibold text-emerald-600">{stats.completed_targets || 0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Daily Target</span><span className="font-semibold">{p.daily_target || 9}</span></div>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden mb-3">
                  <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center gap-2 text-xs flex-wrap mb-3">
                  {Object.entries(stats.by_purpose || {}).slice(0, 4).map(([k, v]) => (
                    <Badge key={k} color={PURPOSE_MAP[k]?.color || "slate"}>{PURPOSE_MAP[k]?.l || k}: {v}</Badge>
                  ))}
                </div>
                <div className="flex gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <Btn variant="secondary" onClick={() => openDetail(p)} className="!py-1 !px-2 !text-xs" data-testid={`plan-view-${p.id}`}>
                    <Eye className="w-3 h-3" /> View
                  </Btn>
                  {has("plans.edit") && p.rep_id === user.id && ["draft", "rejected", "changes_requested"].includes(p.status) && (
                    <Btn variant="secondary" onClick={() => openBuilder(p)} className="!py-1 !px-2 !text-xs">
                      <Pencil className="w-3 h-3" /> Edit
                    </Btn>
                  )}
                  {has("plans.delete") && p.rep_id === user.id && ["draft", "rejected"].includes(p.status) && (
                    <Btn variant="ghost" onClick={() => remove(p)} className="!py-1 !px-2 !text-xs !text-red-600">
                      <Trash2 className="w-3 h-3" />
                    </Btn>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <PlanDetail
        plan={planDetail}
        onClose={() => { setPlanDetail(null); setSelectedPlan(null); }}
        onChange={async () => {
          if (selectedPlan) {
            const { data } = await api.get(`/weekly-plans/${selectedPlan.id}`);
            setPlanDetail(data);
            await load();
          }
        }}
        user={user}
      />

      <PlanBuilder
        open={builderOpen}
        plan={editingPlan}
        doctors={doctors}
        pharmacies={pharmacies}
        onClose={() => { setBuilderOpen(false); setEditingPlan(null); }}
        onSaved={async () => { setBuilderOpen(false); setEditingPlan(null); await load(); }}
        busy={busy}
        setBusy={setBusy}
      />
    </div>
  );
}

// ============= Plan Detail =============
function PlanDetail({ plan, onClose, onChange, user }) {
  const [comment, setComment] = useState("");
  const [decideAction, setDecideAction] = useState(null);
  const [decideComment, setDecideComment] = useState("");
  const has = (p) => user?.permissions?.includes(p);
  if (!plan) return null;

  const isMine = plan.rep_id === user.id;
  const canApprove = has("plans.approve") && plan.status === "pending_approval";
  const canSubmit = has("plans.submit") && isMine && ["draft", "changes_requested"].includes(plan.status);

  const submitPlan = async () => {
    try {
      await api.post(`/weekly-plans/${plan.id}/submit`);
      await onChange();
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  const decide = async () => {
    if (decideAction !== "approve" && !decideComment.trim()) {
      alert("Comment is required");
      return;
    }
    try {
      await api.post(`/weekly-plans/${plan.id}/${decideAction.replace("_", "-")}`, { comment: decideComment });
      setDecideAction(null); setDecideComment("");
      await onChange();
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/weekly-plans/${plan.id}/comments`, { comment });
      setComment("");
      await onChange();
    } catch (err) { alert("Error: " + (err.response?.data?.detail || err.message)); }
  };

  return (
    <Modal open={!!plan} onClose={onClose} title={`Plan · Week of ${plan.week_start}`} maxWidth="max-w-5xl">
      <div className="space-y-4 text-sm">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Badge color={STATUS_COLOR[plan.status]}>{plan.status.replace("_", " ")}</Badge>
            <span className="text-slate-600 dark:text-slate-300">👤 {plan.rep_name}</span>
            <span className="text-slate-500">Targets: <strong>{plan.stats?.total_targets || 0}</strong></span>
            <span className="text-emerald-600">Completed: <strong>{plan.stats?.completed_targets || 0}</strong></span>
            <span className="text-slate-500">{plan.stats?.completion_pct || 0}%</span>
          </div>
          <div className="flex gap-2">
            {canSubmit && <Btn onClick={submitPlan} data-testid="plan-submit-btn"><Send className="w-4 h-4" /> Submit for Approval</Btn>}
            {canApprove && (
              <>
                <Btn variant="success" onClick={() => setDecideAction("approve")} data-testid="plan-approve-btn"><CheckCircle2 className="w-4 h-4" /> Approve</Btn>
                <Btn variant="secondary" onClick={() => setDecideAction("request_changes")} data-testid="plan-changes-btn"><MessageSquare className="w-4 h-4" /> Request Changes</Btn>
                <Btn variant="danger" onClick={() => setDecideAction("reject")} data-testid="plan-reject-btn"><XCircle className="w-4 h-4" /> Reject</Btn>
              </>
            )}
          </div>
        </div>

        {plan.decision_comment && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-3 py-2 text-amber-800 dark:text-amber-200">
            <strong>{plan.decision_by_name}:</strong> {plan.decision_comment}
          </div>
        )}

        {decideAction && (
          <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-3 space-y-2">
            <div className="font-semibold capitalize">{decideAction.replace("_", " ")}</div>
            <Textarea
              rows={2}
              placeholder={decideAction === "approve" ? "Optional comment..." : "Comment is required"}
              value={decideComment}
              onChange={(e) => setDecideComment(e.target.value)}
              data-testid="plan-decision-comment"
            />
            <div className="flex justify-end gap-2">
              <Btn variant="ghost" onClick={() => setDecideAction(null)}>Cancel</Btn>
              <Btn onClick={decide} data-testid="plan-decision-confirm">Confirm</Btn>
            </div>
          </div>
        )}

        {/* Days grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          {(plan.days || []).map((d) => (
            <Card key={d.day_offset} className="p-3">
              <div className="font-bold text-sm mb-1">{DAYS[(d.day_offset) % 7]}</div>
              <div className="text-xs text-slate-500 mb-2">{d.date}</div>
              <div className="space-y-1.5">
                {(d.targets || []).length === 0 && <div className="text-xs text-slate-400">No visits</div>}
                {(d.targets || []).map((t, i) => (
                  <div key={t.id || i} className={`text-xs rounded p-1.5 ${t.completed ? "bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900" : "bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700"}`}>
                    <div className="flex items-center gap-1">
                      {t.completed && <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />}
                      <span className="font-semibold truncate">{t.target_name}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap mt-0.5">
                      <Badge color={t.target_type === "doctor" ? "purple" : "blue"}>{t.target_type}</Badge>
                      {t.classification && <span className="text-slate-500">{t.classification}</span>}
                      {t.planned_time && <span className="text-slate-500">🕐 {t.planned_time}</span>}
                    </div>
                    {t.visit_purpose && (
                      <div className="mt-0.5"><Badge color={PURPOSE_MAP[t.visit_purpose]?.color || "slate"}>{PURPOSE_MAP[t.visit_purpose]?.l || t.visit_purpose}</Badge></div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* Comments */}
        <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
          <div className="font-semibold mb-2">Comments ({(plan.comments || []).length})</div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {(plan.comments || []).length === 0 && <div className="text-xs text-slate-400">No comments yet</div>}
            {(plan.comments || []).map((c) => (
              <div key={c.id} className="rounded bg-slate-50 dark:bg-slate-800/60 p-2 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold">{c.user_name}</span>
                  <Badge color="slate">{c.user_role}</Badge>
                  <span className="text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="text-slate-700 dark:text-slate-200">{c.comment}</div>
              </div>
            ))}
          </div>
          {has("plans.comment") && (
            <div className="mt-2 flex gap-2">
              <input
                className="flex-1 rounded border-slate-300 dark:bg-slate-800 dark:border-slate-700 text-sm px-3 py-1.5"
                placeholder="Add a comment..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && postComment()}
                data-testid="plan-comment-input"
              />
              <Btn onClick={postComment} className="!py-1 !px-3" data-testid="plan-comment-submit"><Send className="w-3 h-3" /></Btn>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ============= Plan Builder =============
function PlanBuilder({ open, plan, doctors, pharmacies, onClose, onSaved, busy, setBusy }) {
  const initial = useMemo(() => {
    if (plan) {
      return {
        week_start: plan.week_start,
        daily_target: plan.daily_target || 9,
        notes: plan.notes || "",
        days: plan.days || [],
      };
    }
    return { week_start: thisWeekSunday(), daily_target: 9, notes: "", days: [0, 1, 2, 3, 4].map((d) => ({ day_offset: d, targets: [] })) };
  }, [plan]);

  const [form, setForm] = useState(initial);
  const [activeDay, setActiveDay] = useState(0);
  const [picker, setPicker] = useState({ type: "pharmacy", filterClass: "", filterCategory: "", q: "" });

  useEffect(() => { setForm(initial); }, [initial, open]);

  const addTarget = (item) => {
    const isDoctor = picker.type === "doctor";
    const target = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      target_type: picker.type,
      target_id: item.id,
      target_name: isDoctor ? item.full_name : item.name,
      classification: isDoctor ? (item.kol_classification || null) : (item.classification || null),
      visit_purpose: "follow_up",
      planned_time: "",
      notes: "",
      completed: false,
    };
    const days = [...form.days];
    const dayIdx = days.findIndex((d) => d.day_offset === activeDay);
    if (dayIdx >= 0) {
      // prevent duplicate same day
      if (days[dayIdx].targets.some((t) => t.target_id === item.id)) return;
      days[dayIdx] = { ...days[dayIdx], targets: [...days[dayIdx].targets, target] };
    } else {
      days.push({ day_offset: activeDay, targets: [target] });
    }
    setForm({ ...form, days });
  };

  const updateTarget = (dayOffset, idx, key, val) => {
    const days = [...form.days];
    const i = days.findIndex((d) => d.day_offset === dayOffset);
    const targets = [...days[i].targets];
    targets[idx] = { ...targets[idx], [key]: val };
    days[i] = { ...days[i], targets };
    setForm({ ...form, days });
  };

  const removeTarget = (dayOffset, idx) => {
    const days = [...form.days];
    const i = days.findIndex((d) => d.day_offset === dayOffset);
    days[i] = { ...days[i], targets: days[i].targets.filter((_, x) => x !== idx) };
    setForm({ ...form, days });
  };

  const filteredPool = useMemo(() => {
    const list = picker.type === "doctor" ? doctors : pharmacies;
    return list.filter((x) => {
      const name = (x.full_name || x.name || "").toLowerCase();
      const cls = picker.type === "doctor" ? x.kol_classification : x.classification;
      if (picker.q && !name.includes(picker.q.toLowerCase())) return false;
      if (picker.filterClass && cls !== picker.filterClass) return false;
      if (picker.filterCategory) {
        if (picker.type === "doctor" && (x.specialty || "").toLowerCase() !== picker.filterCategory.toLowerCase()) return false;
        if (picker.type === "pharmacy" && (x.customer_type || "").toLowerCase() !== picker.filterCategory.toLowerCase()) return false;
      }
      return true;
    }).slice(0, 200);
  }, [picker, doctors, pharmacies]);

  const totalTargets = form.days.reduce((s, d) => s + (d.targets?.length || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        week_start: form.week_start,
        daily_target: parseInt(form.daily_target) || 9,
        notes: form.notes,
        days: form.days.map((d) => ({
          day_offset: d.day_offset,
          targets: (d.targets || []).map((t) => ({
            target_type: t.target_type,
            target_id: t.target_id,
            target_name: t.target_name,
            classification: t.classification,
            visit_purpose: t.visit_purpose,
            planned_time: t.planned_time,
            notes: t.notes,
          })),
        })),
      };
      if (plan) await api.put(`/weekly-plans/${plan.id}`, payload);
      else await api.post("/weekly-plans", payload);
      await onSaved();
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const currentDay = form.days.find((d) => d.day_offset === activeDay) || { targets: [] };
  const dailyTargetWarning = currentDay.targets.length > (parseInt(form.daily_target) || 9);

  return (
    <Modal open={open} onClose={onClose} title={plan ? `Edit Plan · Week of ${plan.week_start}` : "New Weekly Plan"} maxWidth="max-w-6xl">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input label="Week Start (Sunday)" type="date" value={form.week_start} onChange={(e) => setForm({ ...form, week_start: e.target.value })} disabled={!!plan} data-testid="plan-week-start" />
          <Input label="Daily Target (Visits/Day)" type="number" min="1" max="20" value={form.daily_target} onChange={(e) => setForm({ ...form, daily_target: e.target.value })} data-testid="plan-daily-target" />
          <Input label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <div className="flex gap-1 flex-wrap">
          {[0, 1, 2, 3, 4].map((d) => {
            const dayData = form.days.find((x) => x.day_offset === d);
            const count = dayData?.targets?.length || 0;
            return (
              <button
                key={d}
                onClick={() => setActiveDay(d)}
                data-testid={`plan-day-${d}`}
                className={`px-3 py-1.5 rounded text-sm font-medium ${activeDay === d ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800 hover:bg-slate-200"}`}
              >
                {DAYS[d]} <span className="text-xs opacity-75">({count})</span>
              </button>
            );
          })}
          <div className="ms-auto text-sm text-slate-500">Total: <strong className="text-slate-900 dark:text-white">{totalTargets}</strong> visits</div>
        </div>

        {dailyTargetWarning && (
          <div className="text-xs rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 px-3 py-2 text-amber-800 dark:text-amber-200">
            ⚠️ {DAYS[activeDay]} has {currentDay.targets.length} visits — above your daily target of {form.daily_target}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Picker */}
          <Card className="p-3">
            <div className="font-semibold mb-2 text-sm">Add target to <span className="text-blue-700">{DAYS[activeDay]}</span></div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Select value={picker.type} onChange={(e) => setPicker({ ...picker, type: e.target.value, filterClass: "", filterCategory: "" })} data-testid="plan-picker-type">
                <option value="pharmacy">Pharmacy</option>
                <option value="doctor">Doctor</option>
              </Select>
              <Select value={picker.filterClass} onChange={(e) => setPicker({ ...picker, filterClass: e.target.value })} data-testid="plan-picker-class">
                <option value="">All classes</option>
                {picker.type === "doctor"
                  ? ["KOL-A", "KOL-B", "KOL-C", "None"].map((c) => <option key={c} value={c}>{c}</option>)
                  : ["A+", "A", "B", "C"].map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
              <Input placeholder="Category/Specialty filter" value={picker.filterCategory} onChange={(e) => setPicker({ ...picker, filterCategory: e.target.value })} />
              <Input placeholder="Search name..." value={picker.q} onChange={(e) => setPicker({ ...picker, q: e.target.value })} data-testid="plan-picker-search" />
            </div>
            <div className="max-h-72 overflow-y-auto rounded ring-1 ring-slate-200 dark:ring-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
              {filteredPool.length === 0 && <div className="text-center text-xs text-slate-400 py-6">No matches</div>}
              {filteredPool.map((x) => {
                const cls = picker.type === "doctor" ? x.kol_classification : x.classification;
                const cat = picker.type === "doctor" ? x.specialty : x.customer_type;
                return (
                  <button
                    key={x.id}
                    onClick={() => addTarget(x)}
                    data-testid={`plan-pool-${x.id}`}
                    className="w-full text-start px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-950/30 flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{picker.type === "doctor" ? x.full_name : x.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        {cls && <Badge color="slate">{cls}</Badge>}
                        {cat && <span>{cat}</span>}
                        {x.city && <span>· {x.city}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Current day items */}
          <Card className="p-3">
            <div className="font-semibold mb-2 text-sm">{DAYS[activeDay]} plan ({currentDay.targets.length})</div>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {currentDay.targets.length === 0 && <div className="text-center text-xs text-slate-400 py-6">No visits yet · pick from the list →</div>}
              {currentDay.targets.map((t, i) => (
                <div key={t.id} className="rounded ring-1 ring-slate-200 dark:ring-slate-700 p-2 text-xs space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold truncate">{t.target_name}</div>
                    <button onClick={() => removeTarget(activeDay, i)} className="text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <Badge color={t.target_type === "doctor" ? "purple" : "blue"}>{t.target_type}</Badge>
                    {t.classification && <Badge color="slate">{t.classification}</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <select
                      className="rounded border-slate-300 dark:bg-slate-800 dark:border-slate-700 text-xs"
                      value={t.visit_purpose}
                      onChange={(e) => updateTarget(activeDay, i, "visit_purpose", e.target.value)}
                    >
                      {PURPOSES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                    </select>
                    <input
                      type="time"
                      className="rounded border-slate-300 dark:bg-slate-800 dark:border-slate-700 text-xs"
                      value={t.planned_time || ""}
                      onChange={(e) => updateTarget(activeDay, i, "planned_time", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={busy || totalTargets === 0} data-testid="plan-save-btn">{busy ? "Saving..." : (plan ? "Update Plan" : "Save as Draft")}</Btn>
        </div>
      </div>
    </Modal>
  );
}
